import {sendEvent} from '@github-ui/hydro-analytics'
import {PlusIcon, PaperAirplaneIcon} from '@primer/octicons-react'
import {Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import type {List, Nodes} from 'mdast'
import type {MouseEvent, ReactNode} from 'react'
import {use, useSyncExternalStore} from 'react'
import {source} from 'unist-util-source'
import {SKIP, visit} from 'unist-util-visit'

import type {CopilotMarkdownExtension, ReactComponentsExtension} from '../../extension'
import {dataAttrToPropName} from '../../utils'
import {ExtensionContext} from '../ExtensionContext'
import styles from './suggestions.module.css'

// Read suggestions off the kebab-case prop react-markdown exposes, but set the camelCase hast property name
// (matching the issue-link / YamlBlock extensions) so hast converts it to the correct DOM attribute.
const SUGGESTION_ATTRIBUTE = 'data-copilot-suggestion'
const SUGGESTION_PROPERTY = dataAttrToPropName(SUGGESTION_ATTRIBUTE)

/** Matches the invisible marker emitted before a suggestions list, e.g. `<!-- copilot:suggestions -->`. */
const MARKER_REGEX = /^<!--\s*copilot:suggestions\s*-->$/

/** Recursively extract the plain-text content of an mdast node (drops formatting like backticks). */
function mdastToText(node: Nodes): string {
  if ('value' in node && typeof node.value === 'string') return node.value
  if ('children' in node && Array.isArray(node.children)) return node.children.map(mdastToText).join('')
  return ''
}

// The `file` is the VFile unified threads through every transformer; deriving the type from `source` avoids
// taking a direct dependency on `vfile` just for the annotation.
type SourceFile = Parameters<typeof source>[0]

/**
 * Returns a list item's content as markdown, excluding the leading list marker (`1.` / `-`). Slicing from the
 * first child's start offset to the last child's end offset keeps inline formatting (e.g. `code`, **bold**,
 * links) intact, so the chat input receives the formatted source. Falls back to flattened plain text when the
 * parser recorded no positions (e.g. a hand-built tree).
 */
function suggestionMarkdown(item: Nodes, file: SourceFile): string {
  const children = 'children' in item && item.children ? item.children : []
  const firstChild = children.at(0)
  const lastChild = children.at(-1)
  if (firstChild?.position && lastChild?.position) {
    const md = source(file, {start: firstChild.position.start, end: lastChild.position.end})?.trim()
    if (md) return md
  }
  return mdastToText(item).trim()
}

/** Tag a list and its items so the list renders as a set of clickable suggestion chips. */
function tagSuggestionItems(list: List, file: SourceFile): void {
  // Class on the list itself lets us drop the default list markers/indent so the items read as standalone actions.
  list.data = {
    ...list.data,
    hProperties: {
      ...list.data?.hProperties,
      className: styles.suggestionsList,
    },
  }
  for (const item of list.children) {
    const suggestion = suggestionMarkdown(item, file)
    if (!suggestion) continue
    item.data = {
      ...item.data,
      hProperties: {
        ...item.data?.hProperties,
        [SUGGESTION_PROPERTY]: suggestion,
      },
    }
  }
}

// --- Alt/Option key tracking ---------------------------------------------------------------------
// Holding Alt/Option flips a suggestion's default "send" action into "insert into the input without sending".
// All suggestion buttons share a single pair of window listeners via this module-level store, so the affordance
// (icon + tooltip) updates in lockstep no matter how many suggestions are on screen.

let altHeld = false
const altListeners = new Set<() => void>()

function setAltHeld(next: boolean): void {
  if (next === altHeld) return
  altHeld = next
  for (const listener of altListeners) listener()
}

function handleAltKeyEvent(event: KeyboardEvent): void {
  // Not a registrable command — we mirror the live Alt/Option state to toggle the send-vs-insert affordance.
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  setAltHeld(event.altKey)
}

function handleWindowBlur(): void {
  // keyup can be missed once the window loses focus, so reset to avoid a stuck "insert" mode.
  setAltHeld(false)
}

function subscribeAlt(listener: () => void): () => void {
  if (altListeners.size === 0) {
    window.addEventListener('keydown', handleAltKeyEvent)
    window.addEventListener('keyup', handleAltKeyEvent)
    window.addEventListener('blur', handleWindowBlur)
  }
  altListeners.add(listener)
  return () => {
    altListeners.delete(listener)
    if (altListeners.size === 0) {
      window.removeEventListener('keydown', handleAltKeyEvent)
      window.removeEventListener('keyup', handleAltKeyEvent)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }
}

/** Reactively tracks whether Alt/Option is currently held. Always false during SSR. */
function useAltKeyHeld(): boolean {
  return useSyncExternalStore(
    subscribeAlt,
    () => altHeld,
    () => false,
  )
}

const noopSubscribe = () => () => {}

// The Alt key is the Option key on macOS, so name the key the current platform actually has instead of both.
function getModifierKeyLabel(): string {
  return /Macintosh/.test(navigator.userAgent) ? 'Option' : 'Alt'
}

/** Platform-appropriate label for the Alt/Option key. Defaults to "Alt" during SSR. */
function useModifierKeyLabel(): string {
  return useSyncExternalStore(noopSubscribe, getModifierKeyLabel, () => 'Alt')
}

const INSERT_HINT = 'Insert into the input'

// Activating a clickable "Want me to…" follow-up emits a telemetry event with non-sensitive metadata only
// (mode, whether it was sent vs inserted, and the text length). The suggestion text itself is intentionally
// omitted: capturing customer content requires a restricted-dataset event, which needs additional wiring.
const SUGGESTION_CLICK_EVENT = 'copilot_suggestion_followup_click'

function emitSuggestionClickTelemetry(
  suggestion: string,
  submit: boolean,
  mode: 'assistive' | 'immersive' | undefined,
): void {
  sendEvent(SUGGESTION_CLICK_EVENT, {mode, submit, suggestionLength: suggestion.length})
}

function SuggestionListItem({
  suggestion,
  className,
  children,
}: {
  suggestion: string
  className?: string
  children?: ReactNode
}) {
  const {onSuggestionSelect, chatMode} = use(ExtensionContext)
  const isAltHeld = useAltKeyHeld()
  const modifierKey = useModifierKeyLabel()

  // Without a handler there is nowhere to send the text, so render a plain list item.
  if (!onSuggestionSelect) return <li className={className}>{children}</li>

  // Holding Alt/Option switches from "send" to "insert"; reflect that in the icon and tooltip ahead of the
  // click so the resulting behavior is predictable.
  const ActionIcon = isAltHeld ? PlusIcon : PaperAirplaneIcon
  const hint = isAltHeld ? INSERT_HINT : `Send suggestion (hold ${modifierKey} to insert)`

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Read the modifier from the event itself so keyboard activation (e.g. Alt+Enter) is honored too. This is a
    // modifier-aware click, not a global shortcut, so the ui-commands platform does not apply.
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    const submit = !event.altKey
    emitSuggestionClickTelemetry(suggestion, submit, chatMode)
    onSuggestionSelect(suggestion, {submit})
  }

  return (
    <li className={clsx(className, styles.suggestionItem)}>
      {/* `type="description"` keeps the button's accessible name as the suggestion text and adds the action hint. */}
      <Tooltip text={hint} type="description" direction="n">
        <button type="button" className={styles.suggestionButton} onClick={handleClick}>
          <ActionIcon className={styles.suggestionIcon} size={16} />
          <span className={styles.suggestionContent}>{children}</span>
        </button>
      </Tooltip>
    </li>
  )
}

// Module-level constant so the reference stays stable across renders (required by `useCombinedComponents`).
const reactComponents: ReactComponentsExtension = {
  li: (props, fallthrough) => {
    const suggestion = props[SUGGESTION_ATTRIBUTE]
    if (typeof suggestion !== 'string') return fallthrough

    return (
      <SuggestionListItem suggestion={suggestion} className={props.className}>
        {props.children}
      </SuggestionListItem>
    )
  },
}

/**
 * Detects a `<!-- copilot:suggestions -->` marker immediately followed by a list and renders each list item as a
 * clickable action styled like an ice-breaker. Activating a suggestion calls `onSuggestionSelect` from the
 * `ExtensionContext` with the item's markdown source (inline formatting like `code`, **bold**, and links is
 * preserved): a normal click sends it immediately (`submit: true`), while holding Alt/Option pre-fills the chat
 * input without sending (`submit: false`). The extension is inert when no marker is present or when no
 * `onSuggestionSelect` handler is provided, so it is safe to enable everywhere.
 */
export default function suggestionsExtension(): CopilotMarkdownExtension {
  return {
    transformMarkdown: (tree, file) =>
      visit(tree, 'html', (node, index, parent) => {
        if (index == null || !parent) return
        if (!MARKER_REGEX.test(node.value.trim())) return

        // Tag the next list sibling (skipping inconsequential whitespace/text nodes) as clickable suggestions.
        for (const sibling of parent.children.slice(index + 1)) {
          if (sibling.type === 'list') {
            tagSuggestionItems(sibling, file)
            break
          }
          if (sibling.type !== 'html' && sibling.type !== 'text') break
        }

        // Remove the marker node itself. It is an invisible HTML comment, but react-markdown renders raw HTML as
        // literal text (no rehype-raw in the pipeline), so leaving it in would surface the marker to the user.
        // Stripping it here keeps the marker hidden on every surface, regardless of whether a handler is wired up.
        parent.children.splice(index, 1)
        return [SKIP, index]
      }),
    reactComponents,
  }
}
